/**
 * VestingBreakdown.js - Reward Breakdown Component
 * Displays pie chart and progress bars for each reward type
 */

import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import {
  User,
  Target,
  Share2,
  Users,
  BarChart3,
  Lightbulb,
  Lock,
  Zap
} from 'lucide-react';

import { 
  calculateBreakdownPercentages, 
  getRewardProgress,
  REWARD_TYPES 
} from '../utils/vestingHelpers';

import './VestingBreakdown.css';

// Chart colors
const COLORS = ['#FFD700', '#FFA500', '#FF8C00', '#DAA520'];

const VestingBreakdown = ({ data, state }) => {
  const [animatedProgress, setAnimatedProgress] = useState({});
  const [isAnimated, setIsAnimated] = useState(false);

  // Get breakdown data
  const breakdown = data?.breakdown || {
    profile: 0,
    predictions: 0,
    socialShares: 0,
    referrals: 0
  };

  // Prepare pie chart data
  const pieData = [
    { name: 'Profile', value: breakdown.profile || 0, Icon: User },
    { name: 'Predictions', value: breakdown.predictions || 0, Icon: Target },
    { name: 'Social', value: breakdown.socialShares || 0, Icon: Share2 },
    { name: 'Referrals', value: breakdown.referrals || 0, Icon: Users }
  ].filter(item => item.value > 0);

  // Get progress bar data
  const progressData = getRewardProgress(breakdown);

  // Calculate percentages
  const percentages = calculateBreakdownPercentages(breakdown);

  // Animate progress bars on mount
  useEffect(() => {
    setTimeout(() => setIsAnimated(true), 500);
    
    progressData.forEach((item, index) => {
      setTimeout(() => {
        const percentage = item.max > 0 ? (item.current / item.max) * 100 : 0;
        setAnimatedProgress(prev => ({
          ...prev,
          [item.type]: Math.min(100, percentage)
        }));
      }, 300 + index * 150);
    });
  }, [breakdown]);

  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0];
    const percentage = percentages[data.name.toLowerCase()]?.percentage || 0;
    const IconComponent = data.payload.Icon;

    return (
      <div className="breakdown-tooltip">
        <div className="tooltip-name">
          {IconComponent && <IconComponent size={16} />}
          <span>{data.name}</span>
        </div>
        <div className="tooltip-value">
          {data.value.toLocaleString()} TPHILE
        </div>
        <div className="tooltip-percentage">
          {percentage}% of total
        </div>
      </div>
    );
  };

  // Custom legend for pie chart
  const renderCustomLegend = ({ payload }) => {
    return (
      <div className="pie-legend">
        {payload.map((entry, index) => (
          <div key={`legend-${index}`} className="legend-item">
            <span 
              className="legend-color" 
              style={{ background: entry.color }}
            />
            <span className="legend-label">{entry.value}</span>
            <span className="legend-value">
              {percentages[entry.value.toLowerCase()]?.percentage || 0}%
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Get icon for reward type
  const getIcon = (type) => {
    const icons = {
      profile: <User size={20} />,
      predictions: <Target size={20} />,
      socialShares: <Share2 size={20} />,
      referrals: <Users size={20} />
    };
    return icons[type] || <BarChart3 size={20} />;
  };

  // Get max label for reward type
  const getMaxLabel = (type) => {
    const labels = {
      profile: 'One-time reward',
      predictions: 'Ongoing rewards',
      socialShares: 'Daily limit: 5 shares',
      referrals: 'Max 3 referrals'
    };
    return labels[type] || '';
  };

  return (
    <section className={`vesting-breakdown ${isAnimated ? 'animated' : ''}`}>
      <div className="breakdown-header">
        <h2>
          <BarChart3 size={24} />
          Reward Breakdown
        </h2>
        <p className="breakdown-subtitle">
          Distribution of your earned tokens by reward type
        </p>
      </div>

      <div className="breakdown-content">
        {/* Pie Chart Section */}
        <div className="pie-section">
          <div className="pie-container">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="value"
                  animationDuration={1500}
                  animationEasing="ease-out"
                >
                  {pieData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      stroke="rgba(0,0,0,0.3)"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={renderCustomLegend} />
              </PieChart>
            </ResponsiveContainer>
            
            {/* Center label */}
            <div className="pie-center">
              <span className="pie-total-label">Total</span>
              <span className="pie-total-value">
                {Object.values(breakdown).reduce((a, b) => a + b, 0).toLocaleString()}
              </span>
              <span className="pie-total-unit">TPHILE</span>
            </div>
          </div>
        </div>

        {/* Progress Bars Section */}
        <div className="progress-section">
          {progressData.map((item, index) => {
            const progress = animatedProgress[item.type] || 0;
            
            return (
              <div 
                key={item.type} 
                className="progress-item"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                <div className="progress-header">
                  <div className="progress-label">
                    <span className="progress-icon" style={{ color: item.color }}>
                      {getIcon(item.type)}
                    </span>
                    <span className="progress-name">{item.label}</span>
                  </div>
                  <div className="progress-values">
                    <span className="progress-current">{item.current.toLocaleString()}</span>
                    <span className="progress-separator">/</span>
                    <span className="progress-max">
                      {item.type === 'predictions' || item.type === 'socialShares' 
                        ? '∞'
                        : item.max.toLocaleString()
                      }
                    </span>
                  </div>
                </div>
                
                <div className="progress-bar-container">
                  <div className="progress-bar-bg">
                    <div 
                      className="progress-bar-fill"
                      style={{ 
                        width: `${Math.min(100, progress)}%`,
                        background: `linear-gradient(90deg, ${item.color}, ${item.color}aa)`
                      }}
                    >
                      {progress > 0 && <div className="progress-shimmer" />}
                    </div>
                  </div>
                  <span className="progress-percentage">
                    {item.type === 'predictions' || item.type === 'socialShares'
                      ? `${item.current.toLocaleString()} earned`
                      : `${Math.min(100, progress).toFixed(0)}%`
                    }
                  </span>
                </div>

                <div className="progress-meta">
                  {getMaxLabel(item.type)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info note based on state */}
      <div className="breakdown-note">
        {state === 'pre-tge' && (
          <p>
            <span className="note-icon"><Lightbulb size={18} /></span>
            All displayed rewards will be split 50/50 (instant/vested) when TGE triggers
          </p>
        )}
        {state === 'vesting' && (
          <p>
            <span className="note-icon"><Lock size={18} /></span>
            New rewards earned during vesting are added to your vested pool
          </p>
        )}
        {state === 'complete' && (
          <p>
            <span className="note-icon"><Zap size={18} /></span>
            All future rewards are now 100% instantly claimable
          </p>
        )}
      </div>
    </section>
  );
};

export default VestingBreakdown;

